import {
  Field,
  Option,
  SecuritiesFormikValues,
  SecuritiesOptions,
  Step,
  StepEndpointSecurities,
  StepFourSecurities,
} from "../../types";
import styles from "../../styles/securities.module.scss";
import {
  invesmentsKnowlageOptions,
  investmentTools,
  yesOrNoBoolOptions,
} from "../../i18n/securities/options";
import {
  labels,
  placeholders,
  texts,
  title,
} from "../../i18n/securities/stepFour";
import { validationsForLanguage } from "../../validations";
import { holder } from "../../i18n/securities/preconfirmationStep";
import { FormikValues } from "formik";
import { CreateDraftProps, sendStep } from "../../connectors/connectors";
import { addSendedStep, onResetValidationErrorCounts } from "../../reducers/securitiesReducer";
import {
  captureSecuritiesError,
  addStepBreadcrumb,
} from "../../sentry/utils";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";

const StepFour = ({
  language,
  currentHolder,
}: {
  language: "es" | "en";
  currentHolder: number;
}): Step => {
  const validations = validationsForLanguage(language);

  const { label: labelStyle } = styles;

  const year = new Date().getFullYear();
  const yearsOptions: Option[] = [...Array(year + 1).keys()]
    .slice(year - 70)
    .reverse()
    .map((i) => ({ value: `${i}`, label: `${i}` }));

  const toolBoolChecked = (values: FormikValues, toolName: string) =>
    values?.investmentsExperience !== "NONE" &&
    values?.investmentTools?.[toolName]?.bool;

  const investmentsExperienceNotNone = (values: FormikValues) =>
    values?.investmentsExperience && values.investmentsExperience !== "NONE";

  const hasOtherInvestments = (values: FormikValues) =>
    values?.hasOtherInvestmentAccountsBool === "SI";

  const investmentToolField = ({
    name,
    label,
  }: {
    name: string;
    label: string;
  }) =>
    [
      {
        name: `investmentTools.${name}.bool`,
        label: label,
        initialValue: "",
        validation: validations.checkbox,
        type: "checkbox",
        conditionalBool: investmentsExperienceNotNone,
      },
      {
        name: `investmentTools.${name}.knowlage`,
        type: "select",
        label: "",
        hideLabel: true,
        placeholder: placeholders[language].investmentToolKnowlage,
        validation: validations.radioOrSingleSelectRequired(
          invesmentsKnowlageOptions[language]
        ),
        options: invesmentsKnowlageOptions[language],
        selectType: "single",
        initialValue: "",
        columns: 2,
        conditionalBool: (values) =>
          toolBoolChecked(values, `${name.split(".").pop()}`),
      },
      {
        name: `investmentTools.${name}.years`,
        type: "select",
        label: "",
        hideLabel: true,
        placeholder: placeholders[language].investmentToolYears,
        validation: validations.radioOrSingleSelectRequired(yearsOptions),
        options: yearsOptions,
        selectType: "single",
        initialValue: "",
        columns: 2,
        conditionalBool: (values) =>
          toolBoolChecked(values, `${name.split(".").pop()}`),
      },
    ] as Field[];

  return {
    step: 4,
    name: `${title[language]} ${
      currentHolder !== 1 ? `- (${holder[language]} ${currentHolder})` : ""
    }`,
    titleOfStep: title[language],
    label: title[language],
    fields: [
      {
        name: "investmentsExperience",
        type: "select",
        label: labels[language].investmentsExperience,
        placeholder: placeholders[language].investmentsExperience,
        validation: validations.radioOrSingleSelectRequired(
          invesmentsKnowlageOptions[language],
        ),
        options: invesmentsKnowlageOptions[language],
        selectType: "single",
        initialValue: "",
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section1",
        text: (
          <>
            <p className={labelStyle}>{texts[language].section1}</p>
          </>
        ),
        conditionalBool: investmentsExperienceNotNone,
      },

      ...investmentTools[language].flatMap(investmentToolField),

      {
        name: "hasOtherInvestmentAccountsBool",
        label: labels[language].hasOtherInvestmentAccountsBool,
        type: "radio",
        placeholder: "",
        validation: validations.radioOrSingleSelectRequired(
          yesOrNoBoolOptions[language],
        ),
        initialValue: "",
        options: yesOrNoBoolOptions[language],
        conditionalBool: investmentsExperienceNotNone,
      },
      {
        name: "otherInvestmentAccountName",
        type: "text",
        label: labels[language].otherInvestmentAccountName,
        placeholder: placeholders[language].otherInvestmentAccountName,
        validation: validations.alphanumericStringRequired,
        initialValue: "",
        conditionalBool: (values) =>
          investmentsExperienceNotNone(values) && hasOtherInvestments(values),
      },
      {
        name: "otherInvestmentAccountYears",
        type: "text",
        label: labels[language].otherInvestmentAccountYears,
        placeholder: placeholders[language].otherInvestmentAccountYears,
        validation: validations.alphanumericStringRequired,
        initialValue: "",
        conditionalBool: (values) =>
          investmentsExperienceNotNone(values) && hasOtherInvestments(values),
      },

      {
        name: "isAffiliatedWithBroker",
        label: labels[language].isAffiliatedWithBroker,
        type: "radio",
        placeholder: "",
        validation: validations.radioOrSingleSelectRequired(
          yesOrNoBoolOptions[language],
        ),
        initialValue: "",
        options: yesOrNoBoolOptions[language],
      },
      {
        name: "brokerName",
        type: "text",
        label: labels[language].brokerName,
        placeholder: placeholders[language].brokerName,
        validation: validations.alphanumericStringRequired,
        initialValue: "",
        conditional: {
          name: "isAffiliatedWithBroker",
          value: "SI",
        },
      },
      {
        name: "brokerEmployeeName",
        type: "text",
        label: labels[language].brokerEmployeeName,
        placeholder: placeholders[language].brokerEmployeeName,
        validation: validations.alphanumericStringRequired,
        initialValue: "",
        conditional: {
          name: "isAffiliatedWithBroker",
          value: "SI",
        },
      },
      {
        name: "brokerEmployeeRelationship",
        type: "text",
        label: labels[language].brokerEmployeeRelationship,
        placeholder: placeholders[language].brokerEmployeeRelationship,
        validation: validations.alphanumericStringRequired,
        initialValue: "",
        conditional: {
          name: "isAffiliatedWithBroker",
          value: "SI",
        },
      },
    ],
    onContinue: async (values: FormikValues, dispatch, state) => {
      addStepBreadcrumb(4, "enter", { currentHolder });
      try {
        if (!state || !dispatch)
          throw new Error("Error: state or dispatch not found");
        const formValues = values as SecuritiesFormikValues["stepFour"];
        const {
          investmentsExperience,
          otherInvestmentAccountName,
          hasOtherInvestmentAccountsBool,
          otherInvestmentAccountYears,
          isAffiliatedWithBroker,
          brokerName,
          brokerEmployeeName,
          brokerEmployeeRelationship,
        } = formValues;

        const { initialValidationData, sendedSteps } = state.securities;

        const instruments = Object.fromEntries(
          investmentTools[language]
            .filter(({ name }) => !!formValues?.investmentTools?.[name]?.bool)
            .map(
              ({
                name,
                value,
              }: {
                name: SecuritiesOptions["investmentToolsKeys"];
                value: SecuritiesOptions["investmentTool"];
              }) => [
                value,
                {
                  knowledgeLevel:
                    formValues?.investmentTools?.[name]?.knowlage ?? "NONE",
                  since: Number.parseInt(
                    formValues?.investmentTools?.[name]?.years ?? "0",
                  ),
                },
              ],
            ),
        );
        if (
          investmentsExperience !== "NONE" &&
          Object.keys(instruments).length === 0
        ) {
          showToastMessage(texts[language].instrumentsRequired, {
            containerId: "formAlert",
            type: "ERROR",
          });
          throw new Error("Error: Instruments are required");
          return;
        }

        if (currentHolder === 1) {
          const step: StepFourSecurities = {
            "@type": "4_SECURITY",
            investmentKnowledge: {
              knowledgeLevel: investmentsExperience,
              instruments,
              haveAnotherInvestmentAccount:
                hasOtherInvestmentAccountsBool === "SI",
              institutionName:
                hasOtherInvestmentAccountsBool === "SI"
                  ? otherInvestmentAccountName
                  : undefined,
              ...(otherInvestmentAccountYears &&
              hasOtherInvestmentAccountsBool === "SI"
                ? {
                    yearsOfExperienceInAccount: Number.parseInt(
                      otherInvestmentAccountYears,
                    ),
                  }
                : {}),

              ...(isAffiliatedWithBroker === "SI"
                ? {
                    familyAffiliation: {
                      brokerDealerName: brokerName as string,
                      employeeName: brokerEmployeeName as string,
                      relationship: brokerEmployeeRelationship as string,
                    },
                  }
                : {}),
            },
          };

          // Previous sent steps
          const previousSteps = sendedSteps.filter(
            (step: StepEndpointSecurities) => step["@type"] !== "4_SECURITY",
          );

          const body: CreateDraftProps["body"] = {
            companyType: "ALLARIA_SECURITY",
            personType: "PHYSICAL",
            email: initialValidationData.email,
            cuit: initialValidationData.taxId,
            referrer: initialValidationData.referred,
            steps: [...previousSteps, step],
          };

          try {
            await sendStep({ stepId: 4, body });
            dispatch(addSendedStep(step));
            addStepBreadcrumb(4, "complete", { currentHolder });
            dispatch(onResetValidationErrorCounts());
          } catch (error) {
            const requestInfo = {
              url: `${import.meta.env.VITE_BASE_URL}/steps/4`,
              method: "POST",
              body: body,
            };
            throw { error, requestInfo };
          }
        }
      } catch (caught: any) {
        const error = caught?.error ?? caught;
        const requestInfo = caught?.requestInfo;
        console.error("Error step four: ", error);
        captureSecuritiesError({
          error,
          stepId: 4,
          stepName: `${title[language]} ${currentHolder !== 1 ? `- (${holder[language]} ${currentHolder})` : ""}`,
          currentHolder,
          formValues: values,
          requestInfo,
          state,
        });
        throw new Error(
          "SECURITIES_ERROR: Error al enviar los datos en el paso 4",
        );
      }
    },
  };
};

export default StepFour;
